import { useEffect } from 'react';
import { isRouteErrorResponse, useRouteError } from 'react-router';

import { Button } from '@/components/ui/Button';

/**
 * A page's code that could not be downloaded: a lazy route asked for a file
 * the server no longer has. After a deploy the old chunk names are gone; in
 * development the dev server restamps a module it has just reloaded. Either
 * way the page itself is fine and a fresh load fetches the current code.
 * The wording differs per browser, hence the three patterns.
 */
const STALE_CODE = [
  /Failed to fetch dynamically imported module/i,
  /error loading dynamically imported module/i,
  /Importing a module script failed/i,
];

/** Where the last automatic reload is remembered, so a broken server cannot loop. */
const RELOADED_AT = 'neon_stale_code_reload';
/** A second failure within this long after a reload is shown, not reloaded again. */
const RELOAD_GUARD_MS = 15_000;

function isStaleCodeError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : typeof error === 'string' ? error : '';
  return STALE_CODE.some((pattern) => pattern.test(message));
}

function recentlyReloaded(): boolean {
  try {
    const at = Number(sessionStorage.getItem(RELOADED_AT));
    return Number.isFinite(at) && Date.now() - at < RELOAD_GUARD_MS;
  } catch {
    return false;
  }
}

function markReload() {
  try {
    sessionStorage.setItem(RELOADED_AT, String(Date.now()));
  } catch {
    // A browser that refuses storage still gets its one reload.
  }
}

/**
 * Every route's error screen, in place of React Router's developer page.
 *
 * Stale code is mended rather than reported: the page reloads once and the
 * shopper, merchant or owner never sees an error at all. Anything else, or the
 * same failure straight after that reload, gets a plain message and a way on.
 */
export function RouteError() {
  const error = useRouteError();
  const stale = isStaleCodeError(error);
  const willReload = stale && !recentlyReloaded();

  useEffect(() => {
    if (willReload) {
      markReload();
      window.location.reload();
      return;
    }
    console.error(error);
  }, [error, willReload]);

  if (willReload) {
    return (
      <div className="grid min-h-dvh place-items-center p-6 text-sm text-muted" role="status">
        Loading the latest version…
      </div>
    );
  }

  const notFound = isRouteErrorResponse(error) && error.status === 404;
  return (
    <div className="grid min-h-dvh place-items-center bg-white p-6">
      <div className="max-w-md space-y-4 text-center" role="alert">
        <h1 className="text-xl font-semibold text-ink">
          {notFound ? 'This page does not exist' : 'Something went wrong loading this page'}
        </h1>
        <p className="text-sm text-muted">
          {stale
            ? 'A newer version of the site is available. Reload to continue.'
            : notFound
              ? 'The link may be old, or the page may have moved.'
              : 'Please try again. If it keeps happening, let us know what you were doing.'}
        </p>
        <div className="flex justify-center gap-2">
          <Button onClick={() => window.location.reload()}>Reload</Button>
          <Button variant="outline" onClick={() => window.location.assign('/')}>
            Go home
          </Button>
        </div>
      </div>
    </div>
  );
}
